import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { RichSurveyFilterGroupsDisplay } from '~/components/features/RichSurveyFilterGroupsDisplay'
import {
  type CustomerAttribute,
  useSurveyQueryAvailableBrandGroups,
  useSurveyQueryCustomerAttributeGroups,
  useSurveyQueryQuestions,
} from '~/hooks/useSurveyQuery'
import { applyIfExist, type SurveyFilterGroups } from '~/utils'

import { useSurveyId } from '../useSurveyId'

import styles from './TitleRowPopup.css'

export type TitleRowPopupProps = {
  customerAttribute: CustomerAttribute
}

export const TitleRowPopup = ({ customerAttribute }: TitleRowPopupProps) => {
  const { t } = useTranslation()
  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const questions = useSurveyQueryQuestions()

  const surveyId = useSurveyId()
  const link = React.useMemo(() => {
    const customerAttributeGroup = customerAttributeGroups.find(
      (customerAttributeGroup) =>
        customerAttributeGroup.customerAttributes.some(
          ({ id }) => id === customerAttribute.id,
        ),
    )

    return applyIfExist(customerAttributeGroup, ({ id }) =>
      compile('customerAttributeList', {
        surveyId,
        customerAttributeGroupId: id,
      }),
    )
  }, [customerAttribute.id, customerAttributeGroups, surveyId])

  return (
    <VStack padding={4} spacing={4}>
      <VStack spacing={2}>
        <Typography fontWeight={700} variant="normal">
          {customerAttribute.name}
        </Typography>

        <RichSurveyFilterGroupsDisplay
          brandGroups={brandGroups}
          customerAttributeGroups={customerAttributeGroups}
          filterGroups={customerAttribute.filterGroups as SurveyFilterGroups}
          questions={questions}
        />
      </VStack>

      {link && (
        <Link className={styles.filterClearButton} to={link}>
          <StyledButton fillMode="flat">
            {t('edit_customized_segments_definition')}
          </StyledButton>
        </Link>
      )}
    </VStack>
  )
}
